import { useEffect, useMemo, useRef } from 'react';
import { addDays } from 'date-fns';
import type { SchedulerDateRange } from '../types/scheduler.types';
import { WEEK_DAYS } from '../utils/displayWindow';
import {
  dateFilterRangeKey,
  resolveDateFilterRange,
} from '../utils/dateFilterRange';
import type { ActiveDateFilterRange } from '../utils/dateFilterRange';

export type UseDateFilterRangeOptions = {
  taskFilters: Record<string, unknown> | undefined;
  setDateRange: (range: SchedulerDateRange) => void;
  today: Date;
};

export type UseDateFilterRangeResult = {
  /** The active date filter's range, or null when none is set. */
  range: ActiveDateFilterRange | null;
  /** Labels of date filters with only one end set; they are not applied. */
  incomplete: string[];
  /** How many days Prev / Next / Today show and move by. */
  windowDays: number;
};

/**
 * Moves the board to the range a date filter names, and back to the current
 * week when the filter is cleared.
 *
 * Keyed on the days the filter covers, not on the filter object: the context
 * hands out a new object on every unrelated filter change, and following
 * identity would undo the dispatcher's Prev / Next after each one. A range
 * already in context on mount (carried from the task list, a saved filter,
 * a return to the page) is applied the same way as one applied in the modal.
 */
export function useDateFilterRange({
  taskFilters,
  setDateRange,
  today,
}: UseDateFilterRangeOptions): UseDateFilterRangeResult {
  const { range, incomplete } = useMemo(
    () => resolveDateFilterRange(taskFilters),
    [taskFilters]
  );
  const rangeKey = dateFilterRangeKey(range);
  const appliedKeyRef = useRef('');

  useEffect(() => {
    if (rangeKey === appliedKeyRef.current) return;
    const hadRange = appliedKeyRef.current !== '';
    appliedKeyRef.current = rangeKey;

    if (range) {
      setDateRange(range.window);
    } else if (hadRange) {
      setDateRange({ startDate: today, endDate: addDays(today, WEEK_DAYS) });
    }
  }, [rangeKey, range, setDateRange, today]);

  return {
    range,
    incomplete,
    windowDays: range ? range.spanDays : WEEK_DAYS,
  };
}
